import React, { useState, useEffect } from 'react';
import { supabase, db } from '../lib/supabase';
import { printHtml } from '../lib/printHtml';

// ── Public Proposal Page — aspreo.com/proposal/{share_token} ─────────────────
const FONT   = "'Inter',Arial,sans-serif";
const THEMES = {
  default:{ primary:'#2B1DC9' },
  teal:   { primary:'#0D9488' },
  indigo: { primary:'#4F46E5' },
  purple: { primary:'#7C3AED' },
  green:  { primary:'#16A34A' },
  rose:   { primary:'#E11D48' },
  orange: { primary:'#EA580C' },
  slate:  { primary:'#334155' },
  brown:  { primary:'#78350F' },
  navy:   { primary:'#1E3A5F' },
};

export default function PublicProposalPage() {
  const token = window.location.pathname.split('/proposal/')[1];
  const [prop,    setProp]    = useState(null);
  const [biz,     setBiz]     = useState(null);
  const [client,  setClient]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!token) { setError('Invalid link.'); setLoading(false); return; }
    (async () => {
      const { data: propData, error: propErr } = await supabase
        .from('proposals')
        .select('*')
        .eq('share_token', token)
        .eq('is_shared', true)
        .maybeSingle();

      if (propErr) console.warn('proposal fetch:', propErr);
      if (!propData) { setError('This link is invalid or has expired.'); setLoading(false); return; }
      if (propData.share_expires_at && new Date(propData.share_expires_at) < new Date()) {
        setError('This link has expired.'); setLoading(false); return;
      }
      setProp(propData);
      // See PublicInvoicePage.js for why this reads from the view, not
      // profiles directly — plain profiles RLS blocks anon reads entirely.
      const { data: bizData } = await supabase
        .from('public_business_profile')
        .select('biz_name,biz_tagline,biz_logo_url,biz_email,biz_phone,biz_gstin,biz_address,biz_website,plan_id')
        .eq('id', propData.user_id).maybeSingle();
      setBiz(bizData || {});
      if (propData.client_id) {
        const { data: cl } = await supabase
          .from('clients').select('name,company,email,address')
          .eq('id', propData.client_id).maybeSingle();
        setClient(cl);
      }
      setLoading(false);
    })();
  }, [token]);

  const printPDF = () => {
    printHtml(`<!DOCTYPE html><html><head><title>${prop?.title||'Proposal'}</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
      *{box-sizing:border-box;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}
      body{margin:0;font-family:'Inter',Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
      .prop-section{page-break-inside:avoid;break-inside:avoid;}
      .prop-section h2{page-break-after:avoid;break-after:avoid;}
      thead{display:table-header-group;}
      tr{page-break-inside:avoid;break-inside:avoid;}
      @media print{*{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}}
    </style></head><body>${document.getElementById('prop-print-area').innerHTML}</body></html>`);
  };

  if (loading) return <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:FONT}}><div>Loading proposal…</div></div>;
  if (error)   return <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:FONT,padding:24}}><div style={{textAlign:'center',background:'#fff',padding:48,borderRadius:16}}><div style={{fontSize:48,marginBottom:12}}>📄</div><div style={{fontSize:18,fontWeight:700,marginBottom:8}}>Link Unavailable</div><div style={{color:'#666'}}>{error}</div></div></div>;

  const theme   = THEMES[prop.theme || 'default'] || THEMES.default;
  const primary = prop.theme_primary || theme.primary;
  const pageBg  = prop.theme_bg || '#F6F6F8';
  const cardBg  = prop.theme_bg || '#fff';
  const fontColor = prop.theme_font || '#0F0E1A';
  const items   = prop.items || [];
  const subtotal= items.reduce((s,i)=>s+(parseFloat(i.amount)||0),0);
  const taxAmt  = subtotal*((parseFloat(prop.tax_pct)||0)/100);
  const total   = subtotal+taxAmt;

  return (
    <div style={{minHeight:'100vh',background:pageBg,fontFamily:FONT,padding:'32px 16px'}}>
      <div className="no-print" style={{maxWidth:720,margin:'0 auto 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#8888AA'}}>Proposal shared via <strong style={{color:primary}}>Aspreo</strong></div>
        <button onClick={printPDF} style={{background:primary,color:'#fff',border:'none',borderRadius:9,padding:'10px 20px',cursor:'pointer',fontFamily:FONT,fontSize:14,fontWeight:700}}>⬇ Download PDF</button>
      </div>

      <div id="prop-print-area" style={{background:cardBg,borderRadius:16,maxWidth:720,margin:'0 auto',overflow:'hidden',boxShadow:'0 8px 32px rgba(0,0,0,.1)',color:fontColor}}>
        {/* Cover */}
        <div style={{background:primary,color:'#fff',padding:'60px 48px',textAlign:'center'}}>
          {biz?.biz_logo_url&&<img src={biz.biz_logo_url} alt="logo" style={{height:56,marginBottom:16,objectFit:'contain'}}/>}
          <div style={{fontSize:32,fontWeight:900,marginBottom:6}}>{biz?.biz_name||'Business'}</div>
          {biz?.biz_tagline&&<div style={{fontSize:14,opacity:.8,marginBottom:32}}>{biz.biz_tagline}</div>}
          <div style={{display:'flex',justifyContent:'center',gap:0,marginBottom:32,maxWidth:200,margin:'0 auto 32px'}}>
            {['rgba(255,255,255,1)','rgba(255,255,255,.5)','rgba(255,255,255,.25)'].map((c,i)=><div key={i} style={{height:4,flex:1,background:c}}/>)}
          </div>
          <div style={{fontSize:22,fontWeight:800,letterSpacing:'.1em',marginBottom:16}}>{(prop.cover_title||prop.title||'BUSINESS PROPOSAL').toUpperCase()}</div>
          {client&&<div style={{fontSize:16,opacity:.9}}>Prepared for <strong>{client.name}</strong></div>}
          <div style={{fontSize:12,opacity:.6,marginTop:8}}>Date: {new Date(prop.created_at||Date.now()).toLocaleDateString('en-IN')}{prop.valid_until?` · Valid until: ${prop.valid_until}`:''}</div>
        </div>

        <div style={{padding:'36px 48px'}}>
          <div className="prop-section" style={{marginBottom:28}}>
            <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:24,marginBottom:20,borderBottom:'1px solid #eee',paddingBottom:20}}>
              <div style={{fontSize:12,lineHeight:1.8,color:'#555'}}>
                <strong style={{color:primary,fontSize:14}}>{biz?.biz_name||'Your Business'}</strong><br/>
                {biz?.biz_address&&<>{biz.biz_address.split('\n').map((l,i)=><React.Fragment key={i}>{l}<br/></React.Fragment>)}</>}
                {[biz?.biz_email,biz?.biz_phone].filter(Boolean).join(' · ')}{(biz?.biz_email||biz?.biz_phone)&&<br/>}
                {biz?.biz_website&&<>{biz.biz_website}<br/></>}
                {biz?.biz_gstin&&<>GSTIN: {biz.biz_gstin}</>}
              </div>
              <div style={{fontSize:12,color:'#555',textAlign:'right'}}>
                <div><strong>Date:</strong> {new Date(prop.created_at||Date.now()).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>
                {prop.valid_until&&<div style={{marginTop:4}}><strong>Valid Until:</strong> {new Date(prop.valid_until).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>}
              </div>
            </div>
            <div style={{fontSize:12,color:'#555',lineHeight:1.8,marginBottom:16}}>
              <strong style={{color:'#333'}}>To,</strong><br/>
              {client?.name||'[Client Name]'}<br/>
              {client?.company&&<>{client.company}<br/></>}
              {client?.address&&<>{client.address.split('\n').map((l,i)=><React.Fragment key={i}>{l}<br/></React.Fragment>)}</>}
            </div>
            <div style={{fontSize:13,color:'#333'}}><strong>Subject:</strong> Proposal for {prop.title||'Business Services'}</div>
          </div>
          {prop.executive_summary&&<Section title="Executive Summary" primary={primary}><p style={{lineHeight:1.8,whiteSpace:'pre-wrap'}}>{prop.executive_summary}</p></Section>}
          {prop.understanding&&<Section title="Our Understanding" primary={primary} shaded><p style={{lineHeight:1.8,whiteSpace:'pre-wrap'}}>{prop.understanding}</p></Section>}

          {items.length>0&&(
            <Section title="Scope & Investment" primary={primary}>
              <table style={{width:'100%',borderCollapse:'collapse',marginTop:12}}>
                <thead><tr style={{background:primary,color:'#fff'}}>
                  <th style={{padding:'8px 12px',textAlign:'left',fontSize:12}}>Service</th>
                  <th style={{padding:'8px 12px',textAlign:'center',fontSize:12}}>Qty</th>
                  <th style={{padding:'8px 12px',textAlign:'right',fontSize:12}}>Rate</th>
                  <th style={{padding:'8px 12px',textAlign:'right',fontSize:12}}>Amount</th>
                </tr></thead>
                <tbody>
                  {items.map((it,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #eee',background:i%2?'#f9f9f9':'transparent'}}>
                      <td style={{padding:'8px 12px',fontSize:13,fontWeight:600}}>{it.desc}{it.italic_desc&&<div style={{fontStyle:'italic',color:'#666',fontSize:11,fontWeight:400,marginTop:2}}>{it.italic_desc}</div>}</td>
                      <td style={{padding:'8px 12px',textAlign:'center',fontSize:13}}>{it.qty||1}</td>
                      <td style={{padding:'8px 12px',textAlign:'right',fontSize:13}}>₹{parseFloat(it.rate||0).toLocaleString('en-IN')}</td>
                      <td style={{padding:'8px 12px',textAlign:'right',fontSize:13,fontWeight:600}}>₹{parseFloat(it.amount||0).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                  {parseFloat(prop.tax_pct)>0&&<tr><td colSpan={3} style={{textAlign:'right',padding:'8px 12px',color:'#666',fontSize:12}}>GST ({prop.tax_pct}%)</td><td style={{textAlign:'right',padding:'8px 12px',fontSize:12}}>₹{taxAmt.toLocaleString('en-IN')}</td></tr>}
                  <tr style={{borderTop:`2px solid ${primary}`}}>
                    <td colSpan={3} style={{textAlign:'right',padding:'10px 12px',fontWeight:800,fontSize:15,color:primary}}>TOTAL</td>
                    <td style={{textAlign:'right',padding:'10px 12px',fontWeight:800,fontSize:15,color:primary}}>₹{total.toLocaleString('en-IN')}</td>
                  </tr>
                </tbody>
              </table>
            </Section>
          )}

          {prop.why_us&&<Section title={`Why ${biz?.biz_name||'Us'}`} primary={primary} shaded><p style={{lineHeight:1.8,whiteSpace:'pre-wrap'}}>{prop.why_us}</p></Section>}
          {prop.next_steps&&<Section title="Next Steps" primary={primary}><p style={{lineHeight:1.8,whiteSpace:'pre-wrap'}}>{prop.next_steps}</p></Section>}
          {prop.best_practices&&<Section title="Our Approach" primary={primary}><p style={{lineHeight:1.8,whiteSpace:'pre-wrap'}}>{prop.best_practices}</p></Section>}
          {prop.terms&&<div style={{fontSize:12,color:'#666',borderTop:'1px solid #eee',paddingTop:12,marginTop:16}}><strong>Terms:</strong> {prop.terms}</div>}

          {/* Acceptance */}
          <div className="prop-section" style={{borderTop:`2px solid ${primary}`,paddingTop:20,marginTop:24}}>
            <div style={{fontWeight:700,color:primary,marginBottom:10,fontSize:15}}>Proposal Acceptance</div>
            <p style={{fontSize:13,color:'#555',lineHeight:1.7,marginBottom:28}}>
              {prop.acceptance_text||`By signing below, ${client?.name||'[Client]'} accepts this proposal and authorises ${biz?.biz_name||'[Your Company]'} to proceed.`}
            </p>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:40}}>
              {[
                client?.company ? `${client?.name||'[Client Name]'} · ${client.company}` : (client?.name||'[Client Name]'),
                biz?.biz_name||'[Your Company]',
              ].map((n,i)=>(
                <div key={i} style={{borderTop:'1px solid #999',paddingTop:8,fontSize:12,color:'#666'}}>{n}<br/>Date: _______________</div>
              ))}
            </div>
          </div>

          {prop.share_expires_at&&(
            <div style={{fontSize:10,color:'#D97706',textAlign:'right',marginTop:16}}>
              Link expires: {new Date(prop.share_expires_at).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}
            </div>
          )}
          <div style={{fontSize:10,color:'#999',textAlign:'center',marginTop:20,borderTop:'1px solid #eee',paddingTop:16}}>{biz?.biz_name||'Your Business'} · Confidential — for intended recipient only</div>
          {(biz?.plan_id==="hobby"||!biz?.plan_id)&&(
            <div style={{fontSize:10,color:'#aaa',textAlign:'center',marginTop:8}}>Created with <strong>Aspreo</strong> · aspreo.com</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, primary, shaded, children }) {
  return (
    <div className="prop-section" style={{marginBottom:28,background:shaded?'#f9f9f9':'transparent',borderRadius:shaded?8:0,padding:shaded?'16px 20px':0}}>
      <h2 style={{color:primary,fontSize:18,fontWeight:800,margin:'0 0 6px',borderBottom:`2px solid ${primary}`,paddingBottom:6}}>{title}</h2>
      {children}
    </div>
  );
}
